import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Flex,
  Tag,
  message,
  Card,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  InboxOutlined
} from '@ant-design/icons';
import { 
  fetchCountries, 
  createCountry, 
  updateCountry, 
  deleteCountry, 
  toggleCountryStatus, 
  importCountries, 
  fetchRegions
} from '../../utils/api';
import { downloadCountryTemplate } from '../../utils/csvTemplate';

const { TextArea } = Input;
const { Option } = Select;

const CountryManagement = () => {
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    code: '',
    status: 1
  });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  // 批量导入
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importRows, setImportRows] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  useEffect(() => {
    loadCountries();
    loadRegions();
  }, [currentPage, pageSize]);

  const loadCountries = async () => {
    try {
      setLoading(true);
      const data = await fetchCountries({ page: currentPage, pageSize });
      setCountries(data.list || []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('加载国家数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRegions = async () => {
    try {
      const { list: data } = await fetchRegions(1, 100);
      setRegions(data || []);
    } catch (error) {
      console.error('加载地区数据失败:', error);
    }
  };

  const handleCreate = () => {
    setEditingCountry(null);
    setFormData({ name: '', nameEn: '', code: '', status: 1 });
    setShowModal(true);
  };

  const openImportModal = () => {
    setCsvText('');
    setImportRows([]);
    setImportErrors([]);
    setImportModalVisible(true);
  };

  const closeImportModal = () => {
    setImportModalVisible(false);
    setCsvText('');
    setImportRows([]);
    setImportErrors([]);
    setImportLoading(false);
  };

  const normalizeStatus = (val) => {
    const s = String(val || '').trim().toLowerCase();
    if (['1', 'enabled', 'active', 'true', '启用'].includes(s)) return 1;
    if (['0', 'disabled', 'inactive', 'false', '禁用'].includes(s)) return 0;
    return 1;
  };

  const parseCsv = (text) => {
    const errors = [];
    const rows = [];
    const stripBom = (s) => String(s || '').replace(/^\ufeff/, '');
    const lines = stripBom(String(text || '')).trim().split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return { rows, errors };

    const firstCols = lines[0].split(',').map(c => stripBom(c).trim());
    const lcCols = firstCols.map(c => c.toLowerCase());
    const containsName = lcCols.some(c => c.includes('name') || c.includes('名称'));
    const containsNameEn = lcCols.some(c => c.includes('nameen') || c.includes('英文'));
    const containsCode = lcCols.some(c => c.includes('code') || c.includes('代码'));
    const hasHeader = (containsName && containsNameEn && containsCode);
    const startIdx = hasHeader ? 1 : 0;
    
    for (let i = startIdx; i < lines.length; i++) {
      const raw = lines[i];
      const cols = raw.split(',').map(c => stripBom(c).trim());
      const name = cols[0] || '';
      const nameEn = cols[1] || '';
      const code = cols[2] || '';
      const status = normalizeStatus(cols[3] || 'enabled');
      
      if (!name || !nameEn || !code) {
        errors.push(`第 ${i + 1} 行：name/nameEn/code 不能为空`);
        continue;
      }
      rows.push({ name, nameEn, code, status });
    }
    return { rows, errors };
  };

  const handleCsvTextChange = (e) => {
    const text = e.target.value;
    setCsvText(text);
    const { rows, errors } = parseCsv(text);
    setImportRows(rows);
    setImportErrors(errors);
  };

  const decodeCsvFile = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      const tryDecode = (label) => {
        try {
          return new TextDecoder(label, { fatal: false }).decode(buffer);
        } catch (e) {
          return null;
        }
      };
      let text = tryDecode('utf-8');
      const hasReplacement = (s) => (s.match(/\uFFFD/g) || []).length > 0 || /����/.test(s);
      const hasChinese = (s) => /[\u4e00-\u9fa5]/.test(s);
      if (!text || hasReplacement(text)) {
        text = tryDecode('gbk') || tryDecode('gb18030') || text || '';
      }
      if ((!hasChinese(text)) && hasReplacement(text)) {
        text = tryDecode('utf-8') || text;
      }
      return text || '';
    } catch (err) {
      console.error('读取文件失败: ', err);
      message.error('读取文件失败');
      return '';
    }
  };

  const handleCsvFileUpload = (file) => {
    if (!file) return false;
    const isCsv = file.type === 'text/csv' || /\.csv$/i.test(file.name);
    if (!isCsv) {
      message.error('请上传 CSV 文件');
      return Upload.LIST_IGNORE || false;
    }
    const isLt10M = file.size <= 10 * 1024 * 1024;
    if (!isLt10M) {
      message.error('CSV 文件大小不能超过 10MB');
      return Upload.LIST_IGNORE || false;
    }
    (async () => {
      const text = await decodeCsvFile(file);
      setCsvText(text);
      const { rows, errors } = parseCsv(text);
      setImportRows(rows);
      setImportErrors(errors);
    })();
    return false;
  };

  const handleImportSubmit = async () => {
    if (!importRows.length) {
      message.warning('没有可导入的数据');
      return;
    }
    setImportLoading(true);
    const result = await importCountries(importRows);
    setImportLoading(false);
    if (!result?.success) {
      message.error(result?.message || '导入失败');
      return;
    }
    message.success(result?.message || `导入成功（${importRows.length} 条）`);
    closeImportModal();
    await loadCountries();
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setFormData({
      name: country.name || '',
      nameEn: country.nameEn || '',
      code: country.code || '',
      status: country.status ? 1 : 0
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name,
        nameEn: formData.nameEn,
        code: formData.code,
        status: Number(formData.status) === 1 ? 1 : 0
      };
      if (editingCountry) {
        await updateCountry(editingCountry.id, payload);
        message.success('国家更新成功');
      } else {
        await createCountry(payload);
        message.success('国家创建成功');
      }
      setShowModal(false);
      await loadCountries();
    } catch (error) {
      console.error('保存国家数据失败:', error);
      message.error('保存失败，请重试');
    }
  };

  const handleDelete = (countryId) => {
    setCountryToDelete(countryId);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteCountry(countryToDelete);
      message.success('删除成功');
      await loadCountries();
    } catch (error) {
      console.error('删除国家失败:', error);
      message.error('删除失败，请重试');
    } finally {
      setDeleteModalVisible(false);
      setCountryToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setCountryToDelete(null);
  };

  const handleToggleStatus = async (country) => {
    try {
      const result = await toggleCountryStatus(country.id);
      setCountries(prev => prev.map(c =>
        c.id === country.id
          ? { ...c, status: result.status }
          : c
      ));
      message.success(`国家已${result.status === 1 ? '启用' : '禁用'}`);
    } catch (error) {
      console.error('更新国家状态失败:', error);
      message.error('状态切换失败，请重试');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '国家名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text}</div>
          {record.nameEn && <div className="text-gray-500 text-sm">{record.nameEn}</div>}
        </div>
      ),
    },
    {
      title: '国家代码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
    },
    {
      title: '所属地区',
      dataIndex: 'regionId',
      key: 'regionId',
      render: (regionId) => {
        const region = regions.find(r => r.id === regionId);
        return region ? <Tag color="blue">{region.name}</Tag> : <span style={{ color: '#999' }}>未关联</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button
            type={record.status === 1 ? 'default' : 'primary'}
            size="small"
            icon={record.status === 1 ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => handleToggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card title="管理国家数据">
        <Flex wrap gap="small" style={{ marginBottom: '16px' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            新增国家
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            onClick={openImportModal}
          >
            批量导入
          </Button>
        </Flex>
        <Table
          columns={columns}
          dataSource={countries}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            total: totalCount,
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      </Card>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入国家"
        open={importModalVisible}
        onCancel={closeImportModal}
        onOk={handleImportSubmit}
        okText="导入并保存"
        confirmLoading={importLoading}
        width={700}
      >
        <div style={{ marginBottom: 12 }}>
          <Space>
            <Button type="link" onClick={downloadCountryTemplate}>
              下载CSV模板
            </Button>
          </Space>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload.Dragger
              accept=".csv"
              multiple={false}
              showUploadList={false}
              beforeUpload={handleCsvFileUpload}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">拖放 CSV 文件到此处，或点击上传</p>
              <p className="ant-upload-hint">支持的格式: .csv（最大 10MB）</p>
            </Upload.Dragger>

            <div>
              <p style={{ color: '#555', marginTop: 8, marginBottom: 4 }}>CSV 文件格式要求：</p>
              <ul style={{ paddingLeft: 18, color: '#666' }}>
                <li>第一行为表头: name,nameEn,code,status（可选）</li>
                <li>状态支持: 启用/禁用 或 enabled/disabled 或 1/0</li>
                <li>示例: 中国,China,CN,enabled</li>
                <li>注意：地区关联请在地区管理中操作</li>
              </ul>
            </div>

            <div>
              <p style={{ color: '#666', marginBottom: 8 }}>或直接粘贴CSV内容（包含或不包含表头）：name,nameEn,code,status</p>
              <TextArea rows={6} value={csvText} onChange={handleCsvTextChange} placeholder="示例：\n中国,China,CN,enabled\n美国,United States,US,enabled" />
            </div>
          </Space>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Card size="small" title={`预览（有效 ${importRows.length} 条${importErrors.length ? `，错误 ${importErrors.length} 条` : ''}）`}>
            {importErrors.length > 0 && (
              <div style={{ color: '#c00', marginBottom: 8 }}>
                {importErrors.slice(0, 5).map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
                {importErrors.length > 5 && <div>更多错误已隐藏...</div>}
              </div>
            )}
            <Table
              size="small"
              columns={[
                { title: '国家名称', dataIndex: 'name' },
                { title: '英文名', dataIndex: 'nameEn' },
                { title: '代码', dataIndex: 'code' },
                { title: '状态', dataIndex: 'status', render: (s) => (s === 1 ? '启用' : '禁用') }
              ]}
              dataSource={importRows.map((r, i) => ({ ...r, _key: i }))}
              rowKey="_key"
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </div>
      </Modal>

      {/* 模态框 */}
      <Modal
        title={editingCountry ? '编辑国家' : '新增国家'}
        open={showModal}
        onCancel={() => setShowModal(false)}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        width={500}
      >
        <Form layout="vertical" style={{ marginTop: '20px' }}>
          <Form.Item label="国家名称" required>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="请输入国家名称"
            />
          </Form.Item>

          <Form.Item label="英文名称" required>
            <Input
              value={formData.nameEn}
              onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
              placeholder="请输入英文名称"
            />
          </Form.Item>

          <Form.Item label="国家代码" required>
            <Input
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="请输入国家代码（如：CN、US）"
              maxLength={3}
            />
          </Form.Item>

          <Form.Item label="状态">
            <Select
              value={formData.status}
              onChange={(value) => setFormData({ ...formData, status: value })}
              style={{ width: '100%' }}
            >
              <Option value={1}>启用</Option>
              <Option value={0}>禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 删除确认模态框 */}
      <Modal
        title="确认删除"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="确定"
        cancelText="取消"
        okType="danger"
      >
        <p>确定要删除这个国家吗？</p>
      </Modal>
    </div>
  );
};

export default CountryManagement;

